/**
 * Feb 2026 — All-Packages Calendar Diff
 * ------------------------------------------------------------------
 * Scans every ACTIVE package in the workspace for the picked month
 * and reports how many deliverables are missing on the Content
 * Calendar. Aggregates missing counts by category (reels / posts /
 * stories / etc.) and per-package. One-click "Create missing" runs
 * the idempotent generate for every package at once.
 *
 * Replaces the earlier per-package picker flow — a PM no longer has
 * to remember which packages need syncing each month.
 */
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CalendarRange, CheckCircle2, Loader2, PackageOpen, Wand2, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import PackageHoverPreview from "@/components/packages/PackageHoverPreview";

const CATEGORY_LABEL = {
  reels: "Reels", post: "Static Posts", story: "Stories",
  short: "Shorts", carousel: "Carousels", video: "Videos",
  blog: "Blog Posts", email: "Emails", ad: "Ads", other: "Other",
};

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function AllPackagesCalendarSyncDialog({
  open,
  onClose,
  workspaceId,
  defaultMonth,
  onSynced,
}) {
  const [month, setMonth] = useState(defaultMonth || currentMonth());
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [inheritAssignee, setInheritAssignee] = useState(true);

  useEffect(() => {
    if (!open) return;
    setDiff(null);
    setMonth(defaultMonth || currentMonth());
  }, [open, defaultMonth]);

  const runCompare = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/workspaces/${workspaceId}/content-calendar/all-packages-diff`,
        { params: { month }, timeout: 40000 },
      );
      setDiff(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Compare failed")); }
    finally { setLoading(false); }
  };

  const createMissing = async () => {
    if (!diff || !diff.totals?.missing) return;
    setCreating(true);
    try {
      const { data } = await api.post(
        `/workspaces/${workspaceId}/content-calendar/all-packages-generate`,
        { month, inherit_assignee: inheritAssignee },
      );
      toast.success(`Created ${data.created} content item${data.created === 1 ? "" : "s"} across ${data.per_package.length} package${data.per_package.length === 1 ? "" : "s"}${data.tasks_created ? ` · ${data.tasks_created} tasks` : ""}`);
      onSynced && onSynced(data);
      await runCompare();
    } catch (e) { toast.error(apiErrorMessage(e, "Sync failed")); }
    finally { setCreating(false); }
  };

  const monthLabel = useMemo(() => {
    try {
      const [y, m] = month.split("-").map(Number);
      return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
    } catch { return month; }
  }, [month]);

  const totals = diff?.totals || { expected: 0, existing: 0, missing: 0 };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="cc-all-pkg-sync-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Layers className="w-4 h-4" />
            Compare Content Calendar with every active package
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Scans every active package, checks each expected deliverable against what's already on the Content Calendar for the chosen month, and lets you generate any that are missing in one click.
          </p>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Month</label>
              <input type="month" value={month}
                     onChange={(e) => setMonth(e.target.value)}
                     className="block h-9 px-2 border border-border bg-background text-sm mt-1"
                     data-testid="cc-all-pkg-sync-month" />
            </div>
            <Button onClick={runCompare} disabled={loading || !month} variant="outline" size="sm"
                    className="rounded-none h-9" data-testid="cc-all-pkg-sync-compare-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CalendarRange className="w-3.5 h-3.5 mr-1" />}
              {diff ? "Refresh" : "Compare"}
            </Button>
            <label className="inline-flex items-center gap-1.5 text-xs ml-auto cursor-pointer">
              <input type="checkbox" checked={inheritAssignee}
                     onChange={(e) => setInheritAssignee(e.target.checked)}
                     data-testid="cc-all-pkg-sync-inherit" />
              Inherit skill assignees
            </label>
          </div>

          {!diff && !loading && (
            <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border" data-testid="cc-all-pkg-sync-empty">
              Pick a month and click <strong>Compare</strong> to scan every active package.
            </div>
          )}

          {diff && (
            <>
              <div className="grid grid-cols-4 gap-2">
                <Tile label="Packages scanned" value={diff.packages_scanned} />
                <Tile label="With gap" value={diff.packages_with_gap} tone={diff.packages_with_gap > 0 ? "rose" : "muted"} />
                <Tile label="Expected" value={totals.expected} />
                <Tile label="Missing" value={totals.missing} tone={totals.missing > 0 ? "rose" : "emerald"} testid="cc-all-pkg-sync-total-missing" />
              </div>

              {diff.by_category?.length > 0 && (
                <div className="border border-border">
                  <div className="px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground border-b border-border">
                    Missing by category
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-0">
                    {diff.by_category.map((c) => (
                      <div key={c.category} className="px-3 py-2 border-r border-b border-border last:border-r-0 flex items-center justify-between gap-2" data-testid={`cc-all-pkg-sync-cat-${c.category}`}>
                        <div>
                          <div className="text-xs font-medium">{CATEGORY_LABEL[c.category] || c.category}</div>
                          <div className="text-[10px] text-muted-foreground">{c.existing}/{c.expected} on calendar</div>
                        </div>
                        <div className={`text-lg font-mono font-bold ${c.missing > 0 ? "text-rose-700" : "text-emerald-700"}`}>{c.missing}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border border-border">
                <div className="px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground border-b border-border">
                  Per package
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-border/60">
                  {(diff.packages || []).length === 0 && (
                    <div className="text-xs text-muted-foreground text-center py-6 italic">No active packages for {monthLabel}.</div>
                  )}
                  {diff.packages.map((p) => (
                    <div key={p.package_id} className="px-3 py-1.5 flex items-center gap-2" data-testid={`cc-all-pkg-sync-pkg-${p.package_id}`}>
                      <PackageOpen className="w-3 h-3 text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex-1">
                        <PackageHoverPreview pid={p.package_id}>
                          <div className="text-xs font-medium truncate cursor-help hover:underline" title={p.package_name}>{p.package_name}</div>
                        </PackageHoverPreview>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {[p.client_name, p.brand_name].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                      <span className="text-[10px] font-mono text-emerald-700">{p.existing}/{p.expected}</span>
                      <span className={`text-sm font-mono font-semibold ${p.missing > 0 ? "text-rose-700" : "text-muted-foreground"}`}>{p.missing}</span>
                    </div>
                  ))}
                </div>
              </div>

              {totals.missing === 0 && (
                <div className="flex items-center gap-2 text-xs text-emerald-700 border border-emerald-300 bg-emerald-50 px-3 py-2" data-testid="cc-all-pkg-sync-inline-ok">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {monthLabel} is fully synced across all {diff.packages_scanned} packages.
                </div>
              )}
              {totals.missing > 0 && (
                <div className="flex items-center gap-2 text-xs text-amber-800 border border-amber-300 bg-amber-50 px-3 py-2" data-testid="cc-all-pkg-sync-inline-diff">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {totals.missing} item{totals.missing === 1 ? "" : "s"} across {diff.packages_with_gap} package{diff.packages_with_gap === 1 ? "" : "s"} not yet on the calendar for {monthLabel}.
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="cc-all-pkg-sync-close">Close</Button>
          <Button onClick={createMissing}
                  disabled={!diff || totals.missing === 0 || creating}
                  className="rounded-none"
                  data-testid="cc-all-pkg-sync-create-missing">
            {creating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
            Create {totals.missing || ""} missing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Tile({ label, value, tone = "default", testid }) {
  const cls = tone === "rose" ? "border-rose-300 bg-rose-50 text-rose-700"
    : tone === "emerald" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
    : tone === "muted" ? "border-border bg-muted/30 text-muted-foreground"
    : "border-border bg-background";
  return (
    <div className={`border px-3 py-2 ${cls}`} data-testid={testid}>
      <div className="text-[9.5px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-xl font-bold font-mono mt-0.5">{value}</div>
    </div>
  );
}
